import {useEffect, useRef, useState, useCallback} from "react";

/**
 * Lightweight pull-to-refresh for touch devices. Tracks a downward drag that
 * starts when the page is already scrolled to the top, and fires `onRefresh`
 * once the pull passes `threshold`. Returns the live pull distance + state so
 * the caller can render an indicator.
 */
const usePullToRefresh = (onRefresh, {threshold = 70, maxPull = 120} = {}) => {
    const [pull, setPull] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const startY = useRef(null);
    const active = useRef(false);

    const onTouchStart = useCallback((e) => {
        if (window.scrollY <= 0 && !refreshing) {
            startY.current = e.touches[0].clientY;
            active.current = true;
        }
    }, [refreshing]);

    const onTouchMove = useCallback((e) => {
        if (!active.current || startY.current === null) return;
        const delta = e.touches[0].clientY - startY.current;
        if (delta > 0 && window.scrollY <= 0) {
            // Apply resistance so the pull feels natural.
            setPull(Math.min(delta * 0.5, maxPull));
        }
    }, [maxPull]);

    const onTouchEnd = useCallback(() => {
        if (!active.current) return;
        active.current = false;
        startY.current = null;
        if (pull >= threshold && onRefresh) {
            setRefreshing(true);
            onRefresh();
            // The data layer drives the real reload; clear the visual after a beat.
            setTimeout(() => setRefreshing(false), 1200);
        }
        setPull(0);
    }, [pull, threshold, onRefresh]);

    useEffect(() => {
        window.addEventListener("touchstart", onTouchStart, {passive: true});
        window.addEventListener("touchmove", onTouchMove, {passive: true});
        window.addEventListener("touchend", onTouchEnd);
        return () => {
            window.removeEventListener("touchstart", onTouchStart);
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("touchend", onTouchEnd);
        };
    }, [onTouchStart, onTouchMove, onTouchEnd]);

    return {pull, refreshing, threshold};
};

export default usePullToRefresh;
